import { z } from "zod";
import { httpClient } from "../../../lib/http/axios";
import { mapResourcePointListPage, mapResourcePointSpuItem } from "../api/adapters";
import type { ResourcePointListPageRaw, ResourcePointSpuItemRaw } from "../api/adapters";
import type { PointMergeCandidates, PointMergeExecute, PointMergePreview, PointMergeResult, PointMergeSelection } from "../types/merge";

const pointSchema = z.object({
  resource_point_spu_id: z.string(), point_name: z.string(), point_type: z.string(),
  point_sub_type: z.string().nullable(), status: z.string(), is_disabled: z.boolean(),
  country: z.string(), province: z.string().nullable(), city: z.string().nullable(),
  district: z.string().nullable(), address: z.string().nullable(), sku_count: z.number(),
}).passthrough();
const impactSchema = z.object({ label: z.string(), count: z.number().int().nonnegative() });
const config = { timeout: 180_000, "axios-retry": { retries: 0 } } as const;

/** 读取统一成功响应并在仓储边界验证形状。 */
function dataOf<T>(schema: z.ZodType<T>, value: unknown): T {
  return z.object({ code: z.literal("OK"), data: schema }).parse(value).data;
}

/** 从统一列表查询三类可合并资源，类型约束由后端固定。 */
export async function fetchPointMergeCandidates(keyword: string, page: number): Promise<PointMergeCandidates> {
  const { data } = await httpClient.get<unknown>("/resource-points/merge/candidates", {
    params: { keyword: keyword.trim() || undefined, page, page_size: 20 },
  });
  const parsed = dataOf(z.object({ items: z.array(pointSchema), total: z.number(), page: z.number(), page_size: z.number() }), data);
  return mapResourcePointListPage(parsed as ResourcePointListPageRaw);
}

/** 请求绑定明确保留记录与来源集合的合并预览。 */
export async function previewPointMerge(selection: PointMergeSelection): Promise<PointMergePreview> {
  const { data } = await httpClient.post<unknown>("/resource-points/merge/preview", {
    target_id: selection.targetId, source_ids: selection.sourceIds,
  }, config);
  const parsed = dataOf(z.object({
    target: pointSchema, sources: z.array(pointSchema), preview_token: z.string().regex(/^[0-9a-f]{64}$/),
    impacts: z.array(impactSchema), notices: z.array(z.string()), blockers: z.array(z.string()),
  }), data);
  return {
    target: mapResourcePointSpuItem(parsed.target as ResourcePointSpuItemRaw),
    sources: parsed.sources.map((row) => mapResourcePointSpuItem(row as ResourcePointSpuItemRaw)),
    previewToken: parsed.preview_token, impacts: parsed.impacts, notices: parsed.notices, blockers: parsed.blockers,
  };
}

/** 提交预览凭据和稳定请求号，写请求禁用自动重试。 */
export async function executePointMerge(payload: PointMergeExecute): Promise<PointMergeResult> {
  const { data } = await httpClient.post<unknown>("/resource-points/merge/execute", {
    target_id: payload.targetId, source_ids: payload.sourceIds,
    preview_token: payload.previewToken, request_id: payload.requestId,
  }, config);
  const parsed = dataOf(z.object({ request_id: z.string(), target_id: z.string(), target_name: z.string(),
    source_ids: z.array(z.string()), impacts: z.array(impactSchema) }), data);
  return { requestId: parsed.request_id, targetId: parsed.target_id, targetName: parsed.target_name,
    sourceIds: parsed.source_ids, impacts: parsed.impacts };
}
